import { DEFAULT_PATH_PREVIEW_MODE } from '../Components/PathBuilder/pathBuilderTypes';
import { Project } from '../Components/Projects/projects';
import { ApplicationActions, ROBOT_TEMPLATES } from './constants';
import { createDefaultFieldSelection } from './fieldSelection';
import { checkForUnsupportedCharacters, createProjectFromPlatformCode } from './helpers';
import { applyProjectVersionDefaults } from './projectVersions';
import {
  getSaveSlotDisplayProgramName,
  parseBpsProjectMetadataFromPythonSource,
  projectLastModifiedUtcMsToIsoString,
  stripBpsProjectIdFromDeployedPython,
  type SaveSlotLike,
} from './saveSlotProjectId';

const { ACTIONS, DRIVEBASE_DATA } = ApplicationActions.Tabs;
const UNIFIED_SWERVE_SLOT_TEMPLATE_KEY = 'custom_bot';

interface CreateSwerveSlotBackupProjectOptions {
  pythonSource: string;
  slot?: Pick<SaveSlotLike, 'programTitle' | 'bpsProjectId' | 'bpsProjectLastModifiedUtcMs'> | null;
  fallbackName: string;
  projectName?: string;
}

function getBackupProjectName(slot: Pick<SaveSlotLike, 'programTitle'> | null | undefined, fallbackName: string): string {
  const displayName = getSaveSlotDisplayProgramName(slot?.programTitle).trim();
  return displayName || fallbackName;
}

function createLocalSwerveSlotBackupProject(
  name: string,
  fileBaseName: string,
  normalizedPythonSource: string,
  slot: Pick<SaveSlotLike, 'bpsProjectId' | 'bpsProjectLastModifiedUtcMs'> | null | undefined,
  parsedProjectMetadata: ReturnType<typeof parseBpsProjectMetadataFromPythonSource>
): Project {
  const projectLastModifiedUtcMs = parsedProjectMetadata.projectLastModifiedUtcMs ?? slot?.bpsProjectLastModifiedUtcMs;
  const lastModified = projectLastModifiedUtcMsToIsoString(projectLastModifiedUtcMs) ?? new Date().toISOString();

  return applyProjectVersionDefaults({
    program: {
      id: undefined,
      code: normalizedPythonSource,
    },
    name,
    id: parsedProjectMetadata.projectId || slot?.bpsProjectId || crypto.randomUUID(),
    robotTemplate: null,
    file: `${fileBaseName}.py`,
    readOnly: false,
    lastModified,
    dashboardLayout: [],
    paths: [],
    selectedField: createDefaultFieldSelection(),
    selectedPathId: '',
    pathPreviewMode: DEFAULT_PATH_PREVIEW_MODE,
    editorOpenDrawer: false,
    openDrawer: null,
    selectedEditorDrawerTab: null,
    selectedTabs: {
      editorTab: ACTIONS,
      dashboardTab: DRIVEBASE_DATA,
    },
    pathBuilderFieldRotation: 0,
    ...(parsedProjectMetadata.projectVersion && { version: parsedProjectMetadata.projectVersion }),
    ...(parsedProjectMetadata.frameworkVersion && { frameworkVersion: parsedProjectMetadata.frameworkVersion }),
  }) as Project;
}

function getUnifiedSwerveSlotTemplateShareCode(): string {
  const shareCode = ROBOT_TEMPLATES.find((template) => template.key === UNIFIED_SWERVE_SLOT_TEMPLATE_KEY)?.shareCode;
  if (!shareCode) throw new Error(`No share code found for ${UNIFIED_SWERVE_SLOT_TEMPLATE_KEY} template`);
  return shareCode;
}

export async function createSwerveSlotBackupProject({
  pythonSource,
  slot,
  fallbackName,
  projectName,
}: CreateSwerveSlotBackupProjectOptions): Promise<Project> {
  const parsedProjectMetadata = parseBpsProjectMetadataFromPythonSource(pythonSource);
  const parsedProjectName = parsedProjectMetadata.projectName?.trim();
  const parsedProjectLastModifiedUtcMs = parsedProjectMetadata.projectLastModifiedUtcMs ?? slot?.bpsProjectLastModifiedUtcMs;
  const parsedProjectLastModifiedIso = projectLastModifiedUtcMsToIsoString(parsedProjectLastModifiedUtcMs);
  const trimmedProjectName = projectName?.trim();
  const name = trimmedProjectName || parsedProjectName || getBackupProjectName(slot, fallbackName);
  const fileBaseName = checkForUnsupportedCharacters(name);
  const normalizedPythonSource = stripBpsProjectIdFromDeployedPython(pythonSource);
  try {
    const project = await createProjectFromPlatformCode(name, getUnifiedSwerveSlotTemplateShareCode());
    return applyProjectVersionDefaults({
      ...project,
      name,
      id: parsedProjectMetadata.projectId || project.id,
      program: {
        ...project.program,
        code: normalizedPythonSource,
      },
      file: `${fileBaseName}.py`,
      lastModified: parsedProjectLastModifiedIso || project.lastModified,
      ...(parsedProjectMetadata.projectVersion && { version: parsedProjectMetadata.projectVersion }),
      ...(parsedProjectMetadata.frameworkVersion && { frameworkVersion: parsedProjectMetadata.frameworkVersion }),
    }) as Project;
  } catch {
    return createLocalSwerveSlotBackupProject(name, fileBaseName, normalizedPythonSource, slot, parsedProjectMetadata);
  }
}
